import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for self-service payslips (employee view)
 */
export const useMyPayslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/self-service/payslips');
      setPayslips(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load payslips');
      console.error('Error fetching payslips:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  const downloadPayslip = (payslipId) => {
    const token = localStorage.getItem('token');
    window.open(`${BACKEND_URL}/api/reports/payslip/${payslipId}?token=${token}`, '_blank');
  };

  // Stats
  const stats = {
    total: payslips.length,
    latestPeriod: payslips[0] ? `${payslips[0].month}/${payslips[0].year}` : null,
    latestNetPay: payslips[0]?.net_salary || 0,
  };

  return {
    payslips,
    loading,
    error,
    stats,
    fetchPayslips,
    downloadPayslip,
  };
};

/**
 * Hook for self-service leaves (employee view)
 */
export const useMyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/self-service/leaves');
      setLeaves(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load leaves');
      console.error('Error fetching leaves:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  const applyLeave = async (leaveData) => {
    const res = await api.post('/self-service/leaves', leaveData);
    await fetchLeaves();
    return res.data;
  };

  // Stats
  const stats = {
    pending: leaves.filter(l => ['pending_dept_head', 'pending_hr', 'pending'].includes(l.status)).length,
    approved: leaves.filter(l => l.status === 'approved').length,
    rejected: leaves.filter(l => l.status === 'rejected').length,
    total: leaves.length,
  };

  return {
    leaves,
    loading,
    error,
    stats,
    fetchLeaves,
    applyLeave,
  };
};

/**
 * Hook for self-service attendance (employee view)
 * @param {string} initialMonth - Initial month in YYYY-MM format (defaults to current month)
 */
export const useMyAttendance = (initialMonth = null) => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (initialMonth) return initialMonth;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchAttendance = useCallback(async (month = selectedMonth) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/self-service/attendance?month=${month}`);
      setAttendance(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load attendance');
      console.error('Error fetching attendance:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchAttendance(selectedMonth);
  }, [selectedMonth]);

  // Stats for the selected month
  const stats = {
    present: attendance.filter(a => a.status === 'present').length,
    absent: attendance.filter(a => a.status === 'absent').length,
    late: attendance.filter(a => a.status === 'late').length,
    total: attendance.length,
  };

  // Generate month options (last 12 months)
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  return {
    attendance,
    loading,
    error,
    stats,
    selectedMonth,
    setSelectedMonth,
    monthOptions,
    fetchAttendance,
  };
};

/**
 * Hook for self-service profile (employee view)
 */
export const useMyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/self-service/profile');
      setProfile(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load profile');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (updates) => {
    const res = await api.put('/self-service/profile', updates);
    setProfile(res.data);
    return res.data;
  };

  return {
    profile,
    loading,
    error,
    fetchProfile,
    updateProfile,
  };
};

/**
 * Hook for self-service dashboard stats
 */
export const useMyDashboard = () => {
  const [stats, setStats] = useState({
    attendance: { present: 0, absent: 0, late: 0 },
    leaves: { pending: 0, approved: 0, balance: 0 },
    documents: { pending: 0, completed: 0 },
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch multiple endpoints in parallel for dashboard stats
      const [attRes, leaveRes] = await Promise.all([
        api.get('/self-service/attendance'),
        api.get('/self-service/leaves'),
      ]);

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const thisMonthAtt = attRes.data.filter(a => {
        const d = new Date(a.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      });

      setStats({
        attendance: {
          present: thisMonthAtt.filter(a => a.status === 'present').length,
          absent: thisMonthAtt.filter(a => a.status === 'absent').length,
          late: thisMonthAtt.filter(a => a.status === 'late').length,
        },
        leaves: {
          pending: leaveRes.data.filter(l => ['pending_dept_head', 'pending_hr', 'pending'].includes(l.status)).length,
          approved: leaveRes.data.filter(l => l.status === 'approved').length,
          total: leaveRes.data.length,
        },
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, fetchStats };
};

/**
 * Hook for self-service advances (employee view)
 * @param {string} businessId - Current business ID
 */
export const useMyAdvances = (businessId) => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAdvances = useCallback(async () => {
    if (!businessId) {
      setAdvances([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/advances?business_id=${businessId}`);
      setAdvances(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load advances');
      console.error('Error fetching advances:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchAdvances();
  }, [fetchAdvances]);

  const applyAdvance = async (advanceData) => {
    const res = await api.post('/advances', { ...advanceData, business_id: businessId });
    await fetchAdvances();
    return res.data;
  };

  // Stats
  const pendingCount = advances.filter(a => a.status === 'pending').length;
  const approvedCount = advances.filter(a => a.status === 'approved').length;
  const outstanding = advances
    .filter(a => a.status === 'approved')
    .reduce((sum, a) => sum + a.amount - (a.total_repaid || 0), 0);

  const stats = {
    pending: pendingCount,
    approved: approvedCount,
    outstanding,
    total: advances.length,
  };

  return {
    advances,
    loading,
    error,
    stats,
    fetchAdvances,
    applyAdvance,
  };
};
